import { AUDIENCE_CODES } from "@sagot/constants";
import type { MatrixSource } from "../shared/types";

/** Draft access per source: `source_id → sorted audience codes`. */
export type MatrixDraft = Readonly<Record<string, readonly number[]>>;

const PUBLIC = AUDIENCE_CODES.public;

function sorted(codes: readonly number[]): number[] {
  return [...new Set(codes)].sort((a, b) => a - b);
}

export function draftFrom(sources: readonly MatrixSource[]): MatrixDraft {
  return Object.fromEntries(sources.map((s) => [s.source_id, sorted(s.audiences)]));
}

/** Toggles one cell. A source keeps at least one audience (DEC-024), so the last box can't be cleared. */
export function toggleCell(draft: MatrixDraft, sourceId: string, code: number): MatrixDraft {
  const current = draft[sourceId] ?? [];
  const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
  if (next.length === 0) return draft;
  return { ...draft, [sourceId]: sorted(next) };
}

export function isLastAudience(draft: MatrixDraft, sourceId: string, code: number): boolean {
  const current = draft[sourceId] ?? [];
  return current.length === 1 && current[0] === code;
}

/** Only the rows whose audiences changed — the PUT body. */
export function changedEntries(sources: readonly MatrixSource[], draft: MatrixDraft): { source_id: string; audiences: number[] }[] {
  return sources.flatMap((source) => {
    const wanted = draft[source.source_id];
    if (!wanted) return [];
    const before = sorted(source.audiences);
    const same = before.length === wanted.length && before.every((c, i) => c === wanted[i]);
    return same ? [] : [{ source_id: source.source_id, audiences: [...wanted] }];
  });
}

export interface AccessSummary {
  /** Every live source is open to this audience. */
  all: boolean;
  /** Code > public and every public source is open to it ("Everything public"). */
  everythingPublic: boolean;
  /** Source titles listed as chips (public ones are folded into "Everything public"). */
  titles: string[];
  total: number;
}

/** "Can access" line of an audience card, derived from the matrix. */
export function accessSummary(code: number, sources: readonly MatrixSource[]): AccessSummary {
  const open = sources.filter((s) => s.audiences.includes(code));
  const publicSources = sources.filter((s) => s.audiences.includes(PUBLIC));
  const all = sources.length > 0 && open.length === sources.length;
  const everythingPublic = code !== PUBLIC && publicSources.length > 0 && publicSources.every((s) => s.audiences.includes(code));
  const listed = everythingPublic ? open.filter((s) => !s.audiences.includes(PUBLIC)) : open;
  return { all, everythingPublic, titles: listed.map((s) => s.title), total: sources.length };
}
